import React, { useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useExcelView, findActiveWeekIndex } from '../../hooks/client/useExcelView';
import ExcelCell from '../workout/ExcelCell';
import ProgressionDialog from './ProgressionDialog';
import AddExerciseDialog from './AddExerciseDialog';
import { ExcelRow, ExcelCellData } from '../../types/excelView';

interface ExcelViewProps {
  cycleOptions: Array<{ id: number; name: string }>;
  /** Optional fullscreen mode — wider cells + no max-width container. */
  fullscreen?: boolean;
}

const DAY_NAMES_ES: Record<number, string> = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo'
};

/**
 * v2 Excel view — full-cycle table where rows = (day × exercise) and columns
 * = weeks. Backend ships the cycle entity tree nested; this component pivots
 * it client-side via `useExcelView`.
 *
 * Design handoff §4.7.
 */
/** Draggable table row — wraps the exercise row with @dnd-kit sortable so the
 *  coach can grab the drag handle and reorder within/across groups of the
 *  same day. The handle is on the first td; clicking elsewhere on the row
 *  doesn't initiate a drag. */
function DraggableRow({
  row,
  weekCount,
  isPrForRow,
  isDeload,
  savingCells,
  onUpdate,
  onDelete
}: {
  row: ExcelRow;
  weekCount: number;
  isPrForRow: (row: ExcelRow, cell: ExcelCellData | null) => boolean;
  isDeload: (row: ExcelRow, cellIndex: number) => boolean;
  savingCells: Set<number>;
  onUpdate: (id: number, patch: { repetitions?: string; weight?: string }) => Promise<void> | void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };
  void weekCount;
  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td className="et-excel__sticky et-excel__col-exercise">
        <div className="flex align-items-center" style={{ gap: 4 }}>
          <span
            {...listeners}
            className="et-excel__drag-handle"
            aria-label="Mover ejercicio"
            title="Arrastrar para mover"
          >
            <i className="pi pi-bars" />
          </span>
          <span className="flex-1">{row.exerciseName}</span>
          <button
            type="button"
            className="et-excel__row-delete"
            onClick={onDelete}
            title="Eliminar ejercicio"
            aria-label="Eliminar ejercicio"
          >
            <i className="pi pi-times" />
          </button>
        </div>
      </td>
      <td className="et-excel__sticky et-excel__col-sets text-center">{row.cells.find((c) => c?.sets)?.sets ?? '–'}</td>
      {row.cells.map((cell, j) => (
        <ExcelCell
          key={j}
          cell={cell}
          isPr={isPrForRow(row, cell)}
          isDeload={isDeload(row, j)}
          isSaving={cell ? savingCells.has(cell.exerciseInstanceId) : false}
          onUpdate={onUpdate}
        />
      ))}
    </tr>
  );
}

const DAY_FILTER_OPTIONS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' }
];

export const ExcelView: React.FC<ExcelViewProps> = ({ cycleOptions, fullscreen = false }) => {
  const intl = useIntl();
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(cycleOptions[0]?.id ?? null);
  const [progressionVisible, setProgressionVisible] = useState(false);
  const [addExerciseVisible, setAddExerciseVisible] = useState(false);
  const [addGroupVisible, setAddGroupVisible] = useState(false);
  const [addGroupDay, setAddGroupDay] = useState<number | null>(null);
  const [addGroupName, setAddGroupName] = useState('');
  const [addGroupSubmitting, setAddGroupSubmitting] = useState(false);
  const [dayFilter, setDayFilter] = useState<Set<number>>(new Set());

  const {
    data,
    rows: allRows,
    weekNumbers,
    loading,
    error,
    savingCells,
    isPrForRow,
    isDeload,
    updateCell,
    autoProgress,
    copyColumn,
    duplicateWeek,
    addGroup,
    addExercise,
    moveExercise,
    deleteExercise,
    deleteGroup,
    refresh
  } = useExcelView({ cycleId: selectedCycleId });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const activeWeekIdx = useMemo(() => (data?.cycle ? findActiveWeekIndex(data.cycle) : -1), [data]);

  // Apply the day-filter chip selection. Empty filter = show every day.
  const rows = useMemo(() => {
    if (dayFilter.size === 0) return allRows;
    return allRows.filter((r) => dayFilter.has(r.dayNumber));
  }, [allRows, dayFilter]);

  // Days that already exist in the cycle (any week with sessions on dayN).
  const availableDays = useMemo(() => {
    const set = new Set<number>();
    allRows.forEach((r) => set.add(r.dayNumber));
    return Array.from(set).sort((a, b) => a - b);
  }, [allRows]);

  // Rows grouped by dayNumber, for DnD scoping (drag is same-day only).
  const rowsByDay = useMemo(() => {
    const map = new Map<number, ExcelRow[]>();
    rows.forEach((r) => {
      const arr = map.get(r.dayNumber) ?? [];
      arr.push(r);
      map.set(r.dayNumber, arr);
    });
    return map;
  }, [rows]);

  const handleDragEnd = (e: DragEndEvent) => {
    const activeId = e.active.id;
    const overId = e.over?.id;
    if (!overId || activeId === overId) return;
    const active = allRows.find((r) => r.key === activeId);
    const over = allRows.find((r) => r.key === overId);
    if (!active || !over || active.dayNumber !== over.dayNumber) return;

    // Compute new positional indices within the destination group.
    const dayAll = allRows.filter((r) => r.dayNumber === active.dayNumber);
    const sameGroup = active.groupNumber === over.groupNumber;
    const fromGroup = dayAll.filter((r) => r.groupNumber === active.groupNumber);
    const toGroup = dayAll.filter((r) => r.groupNumber === over.groupNumber);
    const fromRowIndex = fromGroup.findIndex((r) => r.key === active.key);
    let toRowIndex = toGroup.findIndex((r) => r.key === over.key);
    if (sameGroup && toRowIndex > fromRowIndex) toRowIndex--; // splice math
    if (fromRowIndex < 0 || toRowIndex < 0) return;

    moveExercise({
      dayNumber: active.dayNumber,
      fromGroupNumber: active.groupNumber,
      fromRowIndex,
      toGroupNumber: over.groupNumber,
      toRowIndex
    });
  };
  void arrayMove; // silences unused-import warning; the BE returns canonical order after move

  // Groups present in each day, deduplicated by groupNumber.
  const groupsByDay = useMemo(() => {
    const map: Record<number, Array<{ groupNumber: number; name?: string | null }>> = {};
    allRows.forEach((r) => {
      const arr = map[r.dayNumber] ?? (map[r.dayNumber] = []);
      if (!arr.some((g) => g.groupNumber === r.groupNumber)) {
        arr.push({ groupNumber: r.groupNumber, name: r.groupName });
      }
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.groupNumber - b.groupNumber));
    return map;
  }, [allRows]);

  const toggleDay = (d: number) => {
    setDayFilter((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  };

  // Empty state — no cycles for this client
  if (cycleOptions.length === 0) {
    return (
      <Card className="text-center" style={{ minHeight: 240 }}>
        <i className="pi pi-table" style={{ fontSize: '2.5rem', color: 'var(--ios-text-tertiary)' }} />
        <h3 className="mt-3">
          <FormattedMessage id="excelView.empty" defaultMessage="Este cliente no tiene un ciclo activo" />
        </h3>
      </Card>
    );
  }

  return (
    <div className={`et-excel ${fullscreen ? 'et-excel--fullscreen' : ''}`}>
      {/* Toolbar */}
      <div className="et-excel__toolbar">
        {cycleOptions.length > 1 && (
          <Dropdown
            value={selectedCycleId}
            options={cycleOptions.map((c) => ({ label: c.name, value: c.id }))}
            onChange={(e) => setSelectedCycleId(e.value)}
            placeholder={intl.formatMessage({
              id: 'excelView.selectCycle',
              defaultMessage: 'Seleccionar ciclo'
            })}
            className="p-inputtext-sm"
          />
        )}
        <Button
          label={intl.formatMessage({
            id: 'excelView.bulk.progression',
            defaultMessage: 'Progresión…'
          })}
          icon="pi pi-arrow-up-right"
          className="p-button-sm p-button-outlined"
          onClick={() => setProgressionVisible(true)}
          disabled={loading || !data}
        />
        <Button
          label={intl.formatMessage({ id: 'excelView.bulk.copyColumn' })}
          icon="pi pi-copy"
          className="p-button-sm p-button-outlined"
          disabled={loading || !data || weekNumbers.length < 2}
          onClick={() => {
            // Default: copy active week (or week 0 if no active) to next
            const sourceIdx = activeWeekIdx >= 0 ? activeWeekIdx : 0;
            copyColumn(sourceIdx);
          }}
        />
        <Button
          label={intl.formatMessage({
            id: 'excelView.bulk.duplicateWeek',
            defaultMessage: 'Duplicar semana'
          })}
          icon="pi pi-clone"
          className="p-button-sm p-button-outlined"
          disabled={loading || !data}
          onClick={() => {
            const sourceIdx = activeWeekIdx >= 0 ? activeWeekIdx : (weekNumbers.length || 1) - 1;
            duplicateWeek(sourceIdx);
          }}
        />
        <Button
          label={intl.formatMessage({ id: 'excelView.addExercise', defaultMessage: 'Agregar ejercicio' })}
          icon="pi pi-plus"
          className="p-button-sm p-button-outlined"
          onClick={() => setAddExerciseVisible(true)}
          disabled={loading || !data || availableDays.length === 0}
        />
        <Button
          label={intl.formatMessage({ id: 'excelView.addGroup', defaultMessage: 'Agregar grupo' })}
          icon="pi pi-th-large"
          className="p-button-sm p-button-outlined"
          onClick={() => {
            setAddGroupDay(availableDays[0] ?? null);
            setAddGroupName('');
            setAddGroupVisible(true);
          }}
          disabled={loading || !data || availableDays.length === 0}
        />
        <Button
          icon="pi pi-refresh"
          className="p-button-sm p-button-text"
          onClick={refresh}
          disabled={loading}
          tooltip={intl.formatMessage({ id: 'common.refresh', defaultMessage: 'Refrescar' })}
        />
      </div>

      {/* Day filter — chip-pill multi-select. Empty selection = show all. */}
      {data && weekNumbers.length > 0 && (
        <div className="et-excel__day-filter">
          <span className="et-excel__day-filter-label">
            <FormattedMessage id="excelView.filter.day" defaultMessage="Filtrar días:" />
          </span>
          {DAY_FILTER_OPTIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => toggleDay(d.value)}
              className={`et-cycle-wizard__chip${dayFilter.has(d.value) ? ' is-active' : ''}`}
            >
              {d.label}
            </button>
          ))}
          {dayFilter.size > 0 && (
            <button type="button" onClick={() => setDayFilter(new Set())} className="et-excel__day-filter-clear">
              <FormattedMessage id="excelView.filter.clear" defaultMessage="Limpiar" />
            </button>
          )}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="et-excel__loading">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        </div>
      )}

      {/* Error state */}
      {error && !data && (
        <Card className="text-center">
          <p className="text-color-secondary">{error}</p>
          <Button
            label={intl.formatMessage({ id: 'common.retry', defaultMessage: 'Reintentar' })}
            icon="pi pi-refresh"
            onClick={refresh}
          />
        </Card>
      )}

      {/* Main grid */}
      {data && !loading && (
        <>
          <div className="et-excel__grid">
            <table className="et-excel__table">
              <thead>
                <tr>
                  <th className="et-excel__sticky et-excel__col-exercise">
                    <FormattedMessage id="excelView.col.exercise" defaultMessage="Ejercicio" />
                  </th>
                  <th className="et-excel__sticky et-excel__col-sets">
                    <FormattedMessage id="excelView.col.sets" defaultMessage="Sets" />
                  </th>
                  {weekNumbers.map((w, i) => (
                    <th
                      key={w}
                      className={i === activeWeekIdx ? 'et-excel__week-active' : ''}
                      onClick={() => copyColumn(i)}
                      title={intl.formatMessage({
                        id: 'excelView.col.copyHint',
                        defaultMessage: 'Click para copiar a la siguiente semana'
                      })}
                    >
                      <FormattedMessage id="cycle.weekGrid.weekN" values={{ n: w }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <tbody>
                  {Array.from(rowsByDay.entries()).map(([dayN, dayRows]) => (
                    <React.Fragment key={`day-${dayN}`}>
                      <tr className="et-excel__day-header">
                        <td colSpan={2 + weekNumbers.length}>{DAY_NAMES_ES[dayN] ?? `Día ${dayN}`}</td>
                      </tr>
                      <SortableContext items={dayRows.map((r) => r.key)} strategy={verticalListSortingStrategy}>
                        {dayRows.map((row, idx) => {
                          const prev = idx > 0 ? dayRows[idx - 1] : null;
                          const showGroupHeader = !prev || prev.groupNumber !== row.groupNumber;
                          // Synthetic rows (buildRows surfaces empty groups so
                          // the coach sees them) have exerciseName === '' and
                          // negative exerciseId — render only the header.
                          const isEmptyGroupMarker = row.exerciseName === '' && row.exerciseId < 0;
                          return (
                            <React.Fragment key={row.key}>
                              {showGroupHeader && (
                                <tr className="et-excel__group-header">
                                  <td colSpan={2 + weekNumbers.length}>
                                    <div className="flex align-items-center justify-content-between">
                                      <span>{row.groupName || `Grupo ${row.groupNumber}`}</span>
                                      <Button
                                        icon="pi pi-trash"
                                        className="p-button-text p-button-sm p-button-danger"
                                        onClick={() => deleteGroup(row.dayNumber, row.groupNumber, row.groupName)}
                                        tooltip={intl.formatMessage({
                                          id: 'excelView.deleteGroup',
                                          defaultMessage: 'Eliminar grupo'
                                        })}
                                        style={{ padding: '0.15rem 0.35rem' }}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {!isEmptyGroupMarker && (
                                <DraggableRow
                                  row={row}
                                  weekCount={weekNumbers.length}
                                  isPrForRow={isPrForRow}
                                  isDeload={isDeload}
                                  savingCells={savingCells}
                                  onUpdate={updateCell}
                                  onDelete={() => deleteExercise(row.dayNumber, row.exerciseId, row.exerciseName)}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </SortableContext>
                    </React.Fragment>
                  ))}
                </tbody>
              </DndContext>
            </table>
          </div>

          {/* Legend */}
          <div className="et-excel__legend">
            <span className="et-excel__legend-item">
              <span className="et-excel__legend-dot et-excel__legend-dot--current" />
              <FormattedMessage id="excelView.legend.currentWeek" />
            </span>
            <span className="et-excel__legend-item">
              <span className="et-excel__indicator et-excel__indicator--pr">★</span>
              <FormattedMessage id="excelView.legend.pr" />
            </span>
            <span className="et-excel__legend-item">
              <span className="et-excel__indicator et-excel__indicator--deload">↓</span>
              <FormattedMessage id="excelView.legend.deload" />
            </span>
          </div>

          {rows.length === 0 && (
            <Card className="text-center mt-2">
              <p className="text-color-secondary">
                {dayFilter.size > 0 ? (
                  <FormattedMessage
                    id="excelView.noSessionsForFilter"
                    defaultMessage="No hay sesiones en los días seleccionados."
                  />
                ) : (
                  <FormattedMessage
                    id="excelView.noSessions"
                    defaultMessage="Este ciclo no tiene sesiones asignadas todavía."
                  />
                )}
              </p>
            </Card>
          )}
        </>
      )}

      <ProgressionDialog
        visible={progressionVisible}
        onHide={() => setProgressionVisible(false)}
        rows={allRows}
        weekNumbers={weekNumbers}
        defaultBaseWeekIndex={activeWeekIdx >= 0 ? activeWeekIdx : 0}
        onApply={autoProgress}
      />

      <AddExerciseDialog
        visible={addExerciseVisible}
        onHide={() => setAddExerciseVisible(false)}
        availableDays={availableDays}
        groupsByDay={groupsByDay}
        defaultDay={availableDays[0]}
        onSubmit={addExercise}
      />

      <Dialog
        header={intl.formatMessage({ id: 'excelView.addGroup.title', defaultMessage: 'Agregar grupo' })}
        visible={addGroupVisible}
        onHide={() => setAddGroupVisible(false)}
        style={{ width: 'min(420px, 95vw)' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label={intl.formatMessage({ id: 'common.cancel' })}
              className="p-button-text"
              onClick={() => setAddGroupVisible(false)}
            />
            <Button
              label={intl.formatMessage({ id: 'common.add', defaultMessage: 'Agregar' })}
              icon="pi pi-plus"
              loading={addGroupSubmitting}
              disabled={addGroupDay == null || addGroupSubmitting}
              onClick={async () => {
                if (addGroupDay == null || addGroupSubmitting) return;
                setAddGroupSubmitting(true);
                try {
                  // Only close the dialog if the add actually succeeded —
                  // otherwise the user loses their typing and the error
                  // toast disappears with nothing to act on.
                  const ok = await addGroup(addGroupDay, addGroupName.trim() || undefined);
                  if (ok) setAddGroupVisible(false);
                } finally {
                  setAddGroupSubmitting(false);
                }
              }}
              style={{ background: 'var(--et-cta-bg)', borderColor: 'var(--et-cta-bg)', color: 'var(--et-cta-fg)' }}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.addGroup.day" defaultMessage="Día" />
            </label>
            <Dropdown
              value={addGroupDay}
              options={availableDays.map((d) => ({
                label:
                  (
                    {
                      1: 'Lunes',
                      2: 'Martes',
                      3: 'Miércoles',
                      4: 'Jueves',
                      5: 'Viernes',
                      6: 'Sábado',
                      7: 'Domingo'
                    } as Record<number, string>
                  )[d] ?? `Día ${d}`,
                value: d
              }))}
              onChange={(e) => setAddGroupDay(e.value)}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.addGroup.name" defaultMessage="Nombre (opcional)" />
            </label>
            <InputText
              value={addGroupName}
              onChange={(e) => setAddGroupName(e.target.value)}
              className="w-full p-inputtext-sm"
              maxLength={80}
              placeholder={intl.formatMessage({
                id: 'excelView.addGroup.namePh',
                defaultMessage: 'Empuje / Tirón / Superserie A …'
              })}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ExcelView;
